import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Point, Rectangle} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../../shared/translation/Translate";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";
import PortalContext from "../../../PortalContext";
import AnnotationEditBoxDialogFields from "../../field/annotation/AnnotationEditBoxDialogFields";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import {InputNumber} from "primereact/inputnumber";
import LengthUnit from "../../../../shared/units/LengthUnit";
import UserSettings from "../../../session/UserSettings";
import Immutable from "../../../../shared/immutable/Immutable";
import {DebugGlobals} from "../settings/DebugDialog";

export interface AnnotationEditBoxDialogProperties extends AbstractDialogProperties, DebugGlobals {
	context: PortalContext;
	parameter?: Point | Rectangle | Array<Point>;
	submit: (result: Point | Rectangle | Array<Point>) => void;
	userSettings?: Immutable<UserSettings>;
	fixedWidth?: boolean;
	fixedHeight?: boolean;
	zoomValue?: number;
	getParent: () => HTMLElement | null;
}

export interface AnnotationEditBoxDialogState extends AbstractDialogState {
	parameter?: Point | Rectangle | Array<Point>;
	originalParameter?: Point | Rectangle | Array<Point>;
	unit: LengthUnit;
}

export class AnnotationEditBoxDialogDefinition extends AbstractDialog<AnnotationEditBoxDialogProperties,
	AnnotationEditBoxDialogState> {
	constructor(props: Readonly<AnnotationEditBoxDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			unit: LengthUnit.MILLIMETER
		};

		this.hide = this.hide.bind(this);
		this.submit = this.submit.bind(this);
		this.onChange = this.onChange.bind(this);
		this.renderError = this.renderError.bind(this);
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(reset: boolean = false): void {
		if (reset && FunctionHelper.exists(this.state.originalParameter)) {
			if (this.state.originalParameter instanceof Array) {
				this.props.submit(this.state.originalParameter.map((point: Point) => point.clone()));
			} else {
				this.props.submit(this.state.originalParameter!.clone());
			}
		}

		this.setState({
			parameter: undefined,
			originalParameter: undefined
		});

		super.hide();
	}

	/**
	 * Render the dialog header
	 */
	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-cog"}/>
					<Translate {...this.props} data="annotation.edit" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide(true)}/>
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.save")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide(true)} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide(true)} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted && FunctionHelper.exists(this.state.parameter) &&
			!FunctionHelper.exists(this.state.exception);
	}

	private renderContent(): ReactNode {
		let parameter: Rectangle | Point | Array<Point> | undefined = this.state.parameter;
		let parent: HTMLElement | null = this.props.getParent();

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(parent)) {
			return null;
		}

		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;

		let width: number = 0;
		let height: number = 0;
		let xPos: number = 0;
		let yPos: number = 0;

		if (parameter instanceof Array) {
			for (let point of parameter.values()) {
				if ((xPos === 0 && point.x! > 0) || point.x! < xPos) {
					xPos = point.x!;
				}

				if ((yPos === 0 && point.y! > 0) || point.y! < yPos) {
					yPos = point.y!;
				}

				if (width === 0 && point.x! > 0) {
					width = point.x!;
				} else if (point.x! < width) {
					width = width - point.x!;
				} else {
					width = point.x! - width;
				}

				if (height === 0 && point.y! > 0) {
					height = point.y!;
				} else if (point.y! < height) {
					height = height - point.y!;
				} else {
					height = point.y! - height;
				}
			}
		} else {
			width = parameter instanceof Rectangle ? FunctionHelper.applyValue(
				parameter.width, Rectangle.getWidthDefault()
			) : Rectangle.getWidthDefault();
			height = parameter instanceof Rectangle ? FunctionHelper.applyValue(
				parameter.height, Rectangle.getHeightDefault()
			) : Rectangle.getHeightDefault();
			xPos = FunctionHelper.applyValue(parameter?.x, Rectangle.getXDefault());
			yPos = FunctionHelper.applyValue(parameter?.y, Rectangle.getYDefault());
		}

		width = UnitHelper.pxToUnit(width, this.state.unit, this.props.portalResolution) / zoomScale;
		height = UnitHelper.pxToUnit(height, this.state.unit, this.props.portalResolution) / zoomScale;
		xPos = UnitHelper.pxToUnit(xPos, this.state.unit, this.props.portalResolution) / zoomScale;
		yPos = UnitHelper.pxToUnit(yPos, this.state.unit, this.props.portalResolution) / zoomScale;

		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let containerWidth: number = UnitHelper.pxToUnit(
			containerRect.width, this.state.unit, this.props.portalResolution
		) / zoomScale;
		let containerHeight: number = UnitHelper.pxToUnit(
			containerRect.height, this.state.unit, this.props.portalResolution
		) / zoomScale;

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-5" htmlFor="control-x">
						<Translate {...this.props} data="annotation.box.left" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-x" step={1} suffix={" " + this.state.unit} showButtons={true}
									 maxFractionDigits={2} minFractionDigits={2} value={xPos} useGrouping={false}
									 min={UnitHelper.serverPxToUnit(
										 Rectangle.getXMin(), this.state.unit, this.props.portalResolution
									 )}
									 max={containerWidth - width}
									 onChange={(e) => this.onChange(
										 AnnotationEditBoxDialogFields.LEFT, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-5" htmlFor="control-y">
						<Translate {...this.props} data="annotation.box.top" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-y" step={1} suffix={" " + this.state.unit} showButtons={true}
									 maxFractionDigits={2} minFractionDigits={2} value={yPos} useGrouping={false}
									 min={UnitHelper.serverPxToUnit(
										 Rectangle.getYMin(), this.state.unit, this.props.portalResolution
									 )}
									 max={containerHeight - height}
									 onChange={(e) => this.onChange(
										 AnnotationEditBoxDialogFields.TOP, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid" hidden={
					!(parameter instanceof Rectangle) ||
					(FunctionHelper.exists(this.props.fixedWidth) && this.props.fixedWidth)
				}>
					<label className="p-col-12 p-sm-5" htmlFor="control-width">
						<Translate {...this.props} data="annotation.box.width" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-width" step={1} suffix={" " + this.state.unit} showButtons={true}
									 maxFractionDigits={2} minFractionDigits={2} value={width} useGrouping={false}
									 min={UnitHelper.serverPxToUnit(
										 Rectangle.getWidthMin(), this.state.unit, this.props.portalResolution
									 )}
									 max={containerWidth - xPos}
									 onChange={(e) => this.onChange(
										 AnnotationEditBoxDialogFields.WIDTH, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid" hidden={
					!(parameter instanceof Rectangle) ||
					(FunctionHelper.exists(this.props.fixedHeight) && this.props.fixedHeight)
				}>
					<label className="p-col-12 p-sm-5" htmlFor="control-height">
						<Translate {...this.props} data="annotation.box.height" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-height" step={1} suffix={" " + this.state.unit} showButtons={true}
									 maxFractionDigits={2} minFractionDigits={2} value={height} useGrouping={false}
									 min={UnitHelper.serverPxToUnit(
										 Rectangle.getHeightMin(), this.state.unit, this.props.portalResolution
									 )}
									 max={containerHeight - yPos}
									 onChange={(e) => this.onChange(
										 AnnotationEditBoxDialogFields.HEIGHT, e.value
									 )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	protected renderDialog(): ReactNode {
		let classList: Array<string> = ["annotation-edit-box-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={() => this.hide(true)}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					{this.renderContent()}
				</div>
			</Dialog>
		);
	}

	private onChange(field: AnnotationEditBoxDialogFields, value: any): void {
		let parent: HTMLElement | null = this.props.getParent();
		let parameter: Point | Rectangle | Array<Point> = this.state.parameter!;
		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;

		if (!FunctionHelper.exists(parent)) {
			return;
		}

		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let containerWidth: number = UnitHelper.pxToUnit(
			containerRect.width, this.state.unit, this.props.portalResolution
		) / zoomScale;
		let containerHeight: number = UnitHelper.pxToUnit(
			containerRect.height, this.state.unit, this.props.portalResolution
		) / zoomScale;

		let width: number = 0;
		let height: number = 0;
		let xPos: number = 0;
		let yPos: number = 0;
		let mod: number = 0;

		if (parameter instanceof Array) {
			for (let point of parameter.values()) {
				if ((xPos === 0 && point.x! > 0) || point.x! < xPos) {
					xPos = point.x!;
				}

				if ((yPos === 0 && point.y! > 0) || point.y! < yPos) {
					yPos = point.y!;
				}

				if (width === 0 && point.x! > 0) {
					width = point.x!;
				} else if (point.x! < width) {
					width = width - point.x!;
				} else {
					width = point.x! - width;
				}

				if (height === 0 && point.y! > 0) {
					height = point.y!;
				} else if (point.y! < height) {
					height = height - point.y!;
				} else {
					height = point.y! - height;
				}
			}
		} else if (parameter instanceof Rectangle) {
			width = FunctionHelper.applyValue(parameter.width, Rectangle.getWidthDefault());
			height = FunctionHelper.applyValue(parameter.height, Rectangle.getHeightDefault());
			xPos = FunctionHelper.applyValue(parameter?.x, Rectangle.getXDefault());
			yPos = FunctionHelper.applyValue(parameter?.y, Rectangle.getYDefault());
		}

		width = UnitHelper.pxToUnit(width, this.state.unit, this.props.portalResolution) / zoomScale;
		height = UnitHelper.pxToUnit(height, this.state.unit, this.props.portalResolution) / zoomScale;
		xPos = UnitHelper.pxToUnit(xPos, this.state.unit, this.props.portalResolution) / zoomScale;
		yPos = UnitHelper.pxToUnit(yPos, this.state.unit, this.props.portalResolution) / zoomScale;

		switch (field) {
			case AnnotationEditBoxDialogFields.LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Point.getXDefault();
				}

				if (parameter instanceof Array) {
					mod = UnitHelper.unitToPx(
						xPos - value, this.state.unit, this.props.portalResolution
					) * zoomScale;

					if (value < containerWidth - width && value > 0) {
						for (let point of parameter) {
							point.x! -= mod;
						}
					}
				} else {
					parameter.x = UnitHelper.unitToPx(
						Math.min(value, containerWidth - width), this.state.unit, this.props.portalResolution
					) * zoomScale;
				}
				break;
			case AnnotationEditBoxDialogFields.TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Point.getYDefault();
				}

				if (parameter instanceof Array) {
					mod = UnitHelper.unitToPx(
						yPos - value, this.state.unit, this.props.portalResolution
					) * zoomScale;

					if (value < containerHeight - height && value > 0) {
						for (let point of parameter) {
							point.y! -= mod;
						}
					}
				} else {
					parameter.y = UnitHelper.unitToPx(
						Math.min(value, containerHeight - height), this.state.unit, this.props.portalResolution
					) * zoomScale;
				}
				break;
			case AnnotationEditBoxDialogFields.WIDTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Rectangle.getWidthDefault();
				}

				if (parameter instanceof Rectangle) {
					parameter.width = UnitHelper.unitToPx(
						Math.min(value, containerWidth - xPos), this.state.unit, this.props.portalResolution
					) * zoomScale;
				}
				break;
			case AnnotationEditBoxDialogFields.HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Rectangle.getHeightDefault();
				}

				if (parameter instanceof Rectangle) {
					parameter.height = UnitHelper.unitToPx(
						Math.min(value, containerHeight - yPos), this.state.unit, this.props.portalResolution
					) * zoomScale;
				}
				break;
		}

		this.setState({
			parameter: parameter
		}, () => {
			this.props.submit(parameter);
		})
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();

		if (FunctionHelper.exists(this.state.parameter)) {
			this.props.submit(this.state.parameter!);
		}

		this.hide();
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let userSettings: UserSettings | undefined = UserSettings.fromJson(this.props.userSettings?.get());

		if (this.props.parameter instanceof Array) {
			this.setState({
				parameter: this.props.parameter?.map((point: Point) => point.clone()),
				originalParameter: this.props.parameter?.map((point: Point) => point.clone()),
				unit: FunctionHelper.applyValue(userSettings?.unit, LengthUnit.MILLIMETER)
			});
		} else {
			this.setState({
				parameter: this.props.parameter?.clone(),
				originalParameter: this.props.parameter?.clone(),
				unit: FunctionHelper.applyValue(userSettings?.unit, LengthUnit.MILLIMETER)
			});
		}
	};
}

const AnnotationEditBoxDialog = React.memo(AnnotationEditBoxDialogDefinition,
	(
		prevProps: Readonly<AnnotationEditBoxDialogProperties>, nextProps: Readonly<AnnotationEditBoxDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AnnotationEditBoxDialog;